/**
 * Quantities and unit costs. Integers only, same discipline as money.
 *
 * Two scales live here, and confusing them is the most expensive mistake available
 * in this codebase, so they are branded separately:
 *
 *   Milli     — a quantity, in thousandths of a unit. 1 lb === 1000. 0.25 lb === 250.
 *   UnitCost  — cost per ONE BASE UNIT, in thousandths of a centavo.
 *               C$15.20 per lb === 1,520,000.
 *
 * Unit cost carries three extra decimal places on purpose. A chicle costing C$0.85 that
 * gets weighted-averaged across a hundred purchases would round itself into nonsense at
 * centavo precision; the error then compounds into every margin report forever.
 */

import { centavos, leerSeparadores, type Centavos } from './money.js';

export type Milli = number & { readonly __brand: 'Milli' };

export type UnitCost = number & { readonly __brand: 'UnitCost' };

export const MILLI_SCALE = 1_000;
export const COST_SCALE = 1_000;

const MAX_MILLI = 1_000_000_000_000;

export function milli(n: number): Milli {
  if (!Number.isInteger(n)) throw new RangeError(`quantity must be an integer of milli-units, got ${n}`);
  if (Math.abs(n) > MAX_MILLI) throw new RangeError(`quantity out of range: ${n}`);
  return n as Milli;
}

export function unitCost(n: number): UnitCost {
  if (!Number.isInteger(n)) throw new RangeError(`unit cost must be an integer, got ${n}`);
  if (n < 0) throw new RangeError(`unit cost cannot be negative: ${n}`);
  return n as UnitCost;
}

export const ZERO_QTY = 0 as Milli;
export const ZERO_COST = 0 as UnitCost;

export function fromUnits(units: number): Milli {
  return milli(Math.round(units * MILLI_SCALE));
}

export function toUnits(m: Milli): number {
  return m / MILLI_SCALE;
}

export function addQty(...xs: Milli[]): Milli {
  return milli(xs.reduce<number>((a, b) => a + b, 0));
}

export function subQty(a: Milli, b: Milli): Milli {
  return milli(a - b);
}

export function negQty(a: Milli): Milli {
  return milli(-a);
}

function bigMulDivHalfUp(a: bigint, b: bigint, d: bigint): bigint {
  if (d === 0n) throw new RangeError('division by zero');
  const n = a * b;
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  const absD = d < 0n ? -d : d;
  if (twice < absD) return q;
  return q + ((n < 0n) !== (d < 0n) ? -1n : 1n);
}

/**
 * Convert a quantity expressed in SALE units into BASE units.
 * factor = how many base units one sale unit contains, in milli.
 * 3 bolsas (saleQty 3000) of a 5 lb bag (factor 5000) => 15 lb (15000).
 */
export function saleToBase(saleQty: Milli, factor: Milli): Milli {
  return milli(Number(bigMulDivHalfUp(BigInt(saleQty), BigInt(factor), BigInt(MILLI_SCALE))));
}

/** Inverse of saleToBase. Used when showing stock in a chosen sale unit. */
export function baseToSale(baseQty: Milli, factor: Milli): Milli {
  if (factor === 0) throw new RangeError('sale unit factor cannot be zero');
  return milli(Number(bigMulDivHalfUp(BigInt(baseQty), BigInt(MILLI_SCALE), BigInt(factor))));
}

/**
 * Line total for a POS line: unit price per SALE unit × quantity in SALE units.
 * C$100.00/bolsa (10000) × 3 bolsas (3000) => C$300.00 (30000).
 */
export function lineTotal(unitPrice: Centavos, saleQty: Milli): Centavos {
  return centavos(Number(bigMulDivHalfUp(BigInt(unitPrice), BigInt(saleQty), BigInt(MILLI_SCALE))));
}

/** Cost of goods sold for a quantity in BASE units, at a given unit cost. */
export function costOf(cost: UnitCost, baseQty: Milli): Centavos {
  return centavos(
    Number(bigMulDivHalfUp(BigInt(cost), BigInt(baseQty), BigInt(MILLI_SCALE) * BigInt(COST_SCALE))),
  );
}

/** Derive a unit cost from a total spend over a base quantity. C$1,520 for 100 lb => 1,520,000. */
export function unitCostFromTotal(total: Centavos, baseQty: Milli): UnitCost {
  if (baseQty === 0) throw new RangeError('cannot derive a unit cost from zero quantity');
  return unitCost(
    Number(bigMulDivHalfUp(BigInt(total), BigInt(MILLI_SCALE) * BigInt(COST_SCALE), BigInt(baseQty))),
  );
}

/**
 * Costo promedio ponderado — the costing method chosen for this system.
 *
 * Recomputed on every receipt of stock. Deliberately NOT lot-aware: lots exist here for
 * expiry and recall traceability only, so the two systems cannot disagree about cost.
 *
 * Existing stock at or below zero (an oversell not yet reconciled) cannot meaningfully be
 * averaged against, so the incoming cost simply becomes the new cost.
 */
export function weightedAverageCost(
  currentQty: Milli,
  currentCost: UnitCost,
  incomingQty: Milli,
  incomingCost: UnitCost,
): UnitCost {
  if (incomingQty <= 0) return currentCost;
  if (currentQty <= 0) return incomingCost;
  const currentValue = BigInt(currentQty) * BigInt(currentCost);
  const incomingValue = BigInt(incomingQty) * BigInt(incomingCost);
  const totalQty = BigInt(currentQty) + BigInt(incomingQty);
  return unitCost(Number(bigMulDivHalfUp(currentValue + incomingValue, 1n, totalQty)));
}

/** Total value of stock on hand, for the inventory valuation report. */
export function stockValue(cost: UnitCost, baseQty: Milli): Centavos {
  return costOf(cost, baseQty);
}

/**
 * Parse a typed quantity. Accepts "2", "2.5", "1/2", "2 1/2" — mixed fractions because
 * someone weighing out rice thinks in half and quarter pounds, not decimals.
 */
export function parseQuantity(input: string): Milli | null {
  const s = leerSeparadores(input.trim(), 3);
  if (s === null || s === '') return null;
  // Past what a quantity can hold, a fraction too («99999999999/1»): unreadable, not an error.
  try {
    return leerCantidad(s);
  } catch {
    return null;
  }
}

function leerCantidad(s: string): Milli | null {
  const mixed = /^(-?\d+)\s+(\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const whole = Number(mixed[1]);
    const num = Number(mixed[2]);
    const den = Number(mixed[3]);
    if (den === 0) return null;
    const sign = whole < 0 ? -1 : 1;
    return milli(Math.round(whole * MILLI_SCALE + sign * (num / den) * MILLI_SCALE));
  }

  const frac = /^(-?\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const den = Number(frac[2]);
    if (den === 0) return null;
    return milli(Math.round((Number(frac[1]) / den) * MILLI_SCALE));
  }

  if (!/^-?\d*\.?\d*$/.test(s) || s === '.' || s === '-') return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  try {
    return fromUnits(n);
  } catch {
    return null;
  }
}

/** Trailing zeros dropped: 3000 -> "3", 2500 -> "2.5", 250 -> "0.25". */
export function formatQuantity(m: Milli, maxDecimals = 3): string {
  const v = m / MILLI_SCALE;
  const s = v.toFixed(maxDecimals);
  const corto = s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  // Less than what is shown, below nothing, is nothing: not «-0».
  return corto === '-0' ? '0' : corto;
}
